import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Navigation from '../components/Navigation';
import Footer from '../../Landing/footer';
import { blogPosts } from '../../../data/blogPosts';
import { Calendar, Clock, ArrowLeft, User, ArrowRight } from 'lucide-react';

const Page = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const ArticleContainer = styled.article`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px 24px 60px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;
  margin-bottom: 40px;
  transition: color 0.2s ease;

  &:hover {
    color: #111827;
  }
`;

const Header = styled.header`
  margin-bottom: 40px;
`;

const Category = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #7c3aed;
  background: #ede9fe;
  padding: 6px 12px;
  border-radius: 100px;
  display: inline-block;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 48px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  color: #6b7280;
  font-size: 15px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 40px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Content = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  line-height: 1.8;
  color: #374151;

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Space Grotesk', sans-serif;
    color: #111827;
    margin-top: 48px;
    margin-bottom: 24px;
    font-weight: 700;
  }

  h2 { font-size: 30px; }
  h3 { font-size: 24px; }

  p { margin-bottom: 24px; }

  ul, ol {
    margin-bottom: 24px;
    padding-left: 24px;
  }

  li { margin-bottom: 8px; }

  blockquote {
    border-left: 4px solid #7c3aed;
    padding-left: 24px;
    margin: 32px 0;
    font-style: italic;
    color: #4b5563;
    font-size: 20px;
  }

  img {
    max-width: 100%;
    border-radius: 12px;
    margin: 32px 0;
  }
  
  a {
    color: #7c3aed;
    text-decoration: underline;
    &:hover { color: #6d28d9; }
  }

  code {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.9em;
    font-family: monospace;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 32px 0;
    font-size: 16px;
    overflow-x: auto;
    display: block;
    
    @media (min-width: 768px) {
      display: table;
    }
  }

  thead {
    background: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
  }

  tbody tr {
    border-bottom: 1px solid #e5e7eb;
    transition: background-color 0.2s ease;

    &:hover {
      background: #f9fafb;
    }
  }

  th {
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    color: #111827;
    font-family: 'Space Grotesk', sans-serif;
  }

  td {
    padding: 12px 16px;
    color: #374151;
  }

  th:first-child,
  td:first-child {
    padding-left: 0;
  }

  th:last-child,
  td:last-child {
    padding-right: 0;
  }
`;

const Tags = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const TagItem = styled.span`
  background: #f3f4f6;
  color: #4b5563;
  padding: 6px 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
`;

const CTASection = styled.div`
  margin-top: 60px;
  padding: 40px;
  background: linear-gradient(135deg, #ede9fe 0%, #f3f4f6 100%);
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c3aed 0%, #a855f7 100%);
  }
`;

const CTATitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
`;

const CTADescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #374151;
  margin-bottom: 24px;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #7c3aed;
  color: #ffffff;
  padding: 14px 28px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(124, 58, 237, 0.2);

  &:hover {
    background: #6d28d9;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px -2px rgba(124, 58, 237, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const TeflTechnicalCaseStudy = React.lazy(() => import('./posts/TeflTechnicalCaseStudy'));
const PremierTeflCaseStudy = React.lazy(() => import('./posts/PremierTeflCaseStudy'));
const AiModelExplosion2025 = React.lazy(() => import('./posts/AiModelExplosion2025'));
const AiSupportPlatformShift = React.lazy(() => import('./posts/AiSupportPlatformShift'));
const SmushLabsVsChatbase = React.lazy(() => import('./posts/SmushLabsVsChatbase'));

const POST_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType<{ post: any }>>> = {
  'tefl-institute-technical-case-study': TeflTechnicalCaseStudy,
  'premier-tefl-case-study': PremierTeflCaseStudy,
  'making-sense-of-ai-model-explosion-2025': AiModelExplosion2025,
  'ai-support-platform-shift': AiSupportPlatformShift,
  'smushlabs-vs-chatbase': SmushLabsVsChatbase,
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [dynamicPostMeta, setDynamicPostMeta] = useState<any>(null);

  const staticPost = blogPosts.find(p => p.slug === slug);
  const post = dynamicPostMeta || staticPost;

  const calculateReadTime = (text: string) => {
    if (!text) return "1 min read";
    const wordCount = text.trim().split(/\s+/).length;
    const readTimeMinutes = Math.ceil(wordCount / 225);
    return `${readTimeMinutes} min read`;
  };

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      if (staticPost && staticPost.type !== 'react') {
        try {
          const res = await fetch(`/blog/${slug}.md`);
          if (res.ok) {
            const text = await res.text();
            setContent(text);
          } else {
            throw new Error('Static post markdown not found');
          }
        } catch (err) {
          console.error(err);
        }
        setLoading(false);
      } else if (!staticPost) {
        try {
          const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
          const res = await fetch(`${baseUrl}/api/outrank/articles/${slug}`);
          if (res.ok) {
            const data = await res.json();
            setDynamicPostMeta({
              id: data.slug,
              slug: data.slug,
              title: data.title,
              description: data.meta_description || '',
              date: data.published_at ? new Date(data.published_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              author: 'Asif Farhan Khan',
              category: 'Blog',
              readTime: calculateReadTime(data.content_markdown),
              tags: data.tags || [],
              imageUrl: data.image_url,
              type: 'markdown'
            });
            setContent(data.content_markdown || '');
          }
        } catch (err) {
          console.error('Failed to fetch dynamic article:', err);
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchPost();
    }
  }, [slug, staticPost]);

  if (loading) {
    return (
      <Page>
        <Navigation />
        <ArticleContainer>
          <div style={{ textAlign: 'center', padding: '100px 0', fontSize: '18px', color: '#6b7280' }}>
            Loading article...
          </div>
        </ArticleContainer>
        <Footer />
      </Page>
    );
  }

  if (!post) {
    return (
      <Page>
        <Navigation />
        <ArticleContainer>
          <h1>Post not found</h1>
          <Link to="/blog">Return to Blog</Link>
        </ArticleContainer>
        <Footer />
      </Page>
    );
  }

  if (post.type === 'react' && slug && POST_COMPONENTS[slug]) {
    const Component = POST_COMPONENTS[slug];
    return (
      <React.Suspense fallback={<div>Loading...</div>}>
        <Component post={post} />
      </React.Suspense>
    );
  }

  return (
    <Page>
      <Helmet>
        <title>{post.title} | SmushChat Blog</title>
        <meta name="description" content={post.description} />
      </Helmet>
      <Navigation />

      <ArticleContainer>
        <BackLink to="/blog">
          <ArrowLeft size={20} />
          Back to Blog
        </BackLink>

        <Header>
          <Category>{post.category}</Category>
          <Title>{post.title}</Title>
          <Meta>
            <MetaItem>
              <User size={18} />
              {post.author}
            </MetaItem>
            <MetaItem>
              <Calendar size={18} />
              {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </MetaItem>
            <MetaItem>
              <Clock size={18} />
              {post.readTime}
            </MetaItem>
          </Meta>
        </Header>

        <Content>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{content}</ReactMarkdown>
        </Content>

        <Tags>
          {post.tags.map((tag: string) => (
            <TagItem key={tag}>#{tag}</TagItem>
          ))}
        </Tags>

        {post.ctaDescription && (
          <CTASection>
            <CTATitle>Ready to get started?</CTATitle>
            <CTADescription>{post.ctaDescription}</CTADescription>
            <CTAButton to="/#demo">
              Request a Custom Demo
              <ArrowRight size={20} />
            </CTAButton>
          </CTASection>
        )}
      </ArticleContainer>

      <Footer />
    </Page>
  );
};

export default BlogPost;
